import { createContext, useContext, useState, useCallback } from 'react';
import { Joyride, ACTIONS, EVENTS, STATUS } from 'react-joyride';
import {
  LayoutDashboard,
  FileText,
  Map,
  Route,
  GraduationCap,
  Target,
  Briefcase,
  UserCircle2,
  Rocket,
  Lock,
  Lightbulb,
  ChevronRight,
  Users,
} from 'lucide-react';

const TourContext = createContext();

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within TourProvider');
  }
  return context;
};

// ---------------------------------------------------------------------------
// Brand tokens — pulled from the app's own UI (sidebar navy + accent red)
// ---------------------------------------------------------------------------
const COLORS = {
  navy: '#0F1830',
  navyLight: '#16213E',
  accent: '#B5433C',
  accentSoft: '#F6E9E8',
  border: '#E6E8EE',
  textBody: '#4B5563',
  textMuted: '#8A8F9B',
  bg: '#FFFFFF',
  bgAlt: '#F7F8FA',
  blue: {
    bg: '#EFF6FF',
    border: '#BFDBFE',
    text: '#1E40AF',
  },
  purple: {
    bg: '#F5F3FF',
    border: '#DDD6FE',
    text: '#6B21A8',
  },
};

// Small, restrained tag colors — used only for the three profile types so
// they stay visually distinct and scannable across steps.
const TAGS = {
  student: { bg: '#EEF2FF', fg: '#4338CA', label: 'Student' },
  fresher: { bg: '#ECFDF5', fg: '#047857', label: 'Fresher' },
  professional: { bg: '#FEF3E9', fg: '#B5433C', label: 'Professional' },
};

// ---------------------------------------------------------------------------
// Shared building blocks
// ---------------------------------------------------------------------------
const IconBadge = ({ Icon }) => (
  <div
    style={{
      width: 34,
      height: 34,
      borderRadius: 8,
      background: COLORS.accentSoft,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    <Icon size={17} strokeWidth={2} color={COLORS.accent} />
  </div>
);

const TourHeader = ({ icon: Icon, eyebrow, title }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
    {Icon && <IconBadge Icon={Icon} />}
    <div>
      {eyebrow && (
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: COLORS.accent,
            marginBottom: 3,
            lineHeight: 1,
          }}
        >
          {eyebrow}
        </div>
      )}
      <h3 style={{ fontSize: 18, fontWeight: 700, color: COLORS.navy, margin: 0, lineHeight: 1.3 }}>
        {title}
      </h3>
    </div>
  </div>
);

const TourBody = ({ children }) => (
  <p style={{ fontSize: 14, color: COLORS.textBody, lineHeight: 1.6, margin: '0 0 14px 0' }}>
    {children}
  </p>
);

const TourList = ({ items }) => (
  <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
    {items.map((item, i) => (
      <li
        key={i}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          padding: '7px 0',
          borderTop: i === 0 ? 'none' : `1px solid ${COLORS.border}`,
          fontSize: 13.5,
          color: COLORS.textBody,
          lineHeight: 1.5,
        }}
      >
        <ChevronRight size={14} color={COLORS.accent} style={{ marginTop: 3, flexShrink: 0 }} />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const TourNote = ({ children, locked }) => (
  <div
    style={{
      marginTop: 14,
      display: 'flex',
      alignItems: 'flex-start',
      gap: 8,
      padding: '10px 12px',
      borderRadius: 6,
      background: locked ? COLORS.bgAlt : COLORS.accentSoft,
      borderLeft: `3px solid ${locked ? COLORS.navy : COLORS.accent}`,
      fontSize: 12.5,
      color: COLORS.textBody,
    }}
  >
    {locked ? (
      <Lock size={14} color={COLORS.navy} style={{ marginTop: 1, flexShrink: 0 }} />
    ) : (
      <Lightbulb size={14} color={COLORS.accent} style={{ marginTop: 1, flexShrink: 0 }} />
    )}
    <span>
      <strong style={{ color: COLORS.navy }}>{locked ? 'Unlocks after: ' : 'Tip: '}</strong>
      {children}
    </span>
  </div>
);

const StepBadge = ({ n, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
    <span
      style={{
        width: 22,
        height: 22,
        borderRadius: '50%',
        background: COLORS.navyLight,
        color: '#fff',
        fontSize: 11,
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {n}
    </span>
    <span style={{ fontSize: 14, color: COLORS.navy, fontWeight: 600 }}>{label}</span>
  </div>
);

const ProfileTag = ({ type }) => {
  const t = TAGS[type];
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: 11,
        fontWeight: 700,
        color: t.fg,
        background: t.bg,
        borderRadius: 4,
        padding: '3px 8px',
        marginBottom: 6,
      }}
    >
      {t.label}
    </span>
  );
};

const ProfileSection = ({ type, items }) => (
  <div style={{ marginBottom: 12 }}>
    <ProfileTag type={type} />
    <TourList items={items} />
  </div>
);

// ---------------------------------------------------------------------------
// Steps
// NOTE: inline "maxWidth" values on step content wrappers were removed in
// favour of `width: '100%'` so the content always fills whatever width the
// Joyride tooltip container gives it (which is now itself responsive — see
// the `styles.tooltip` block below). This is what stops the card from ever
// being wider than the viewport on small/desktop-narrow screens.
// ---------------------------------------------------------------------------
const steps = [
  {
    target: 'body',
    placement: 'center',
    content: (
      <div style={{ width: '100%' }}>
        <TourHeader icon={Rocket} eyebrow="Welcome" title="Welcome to F.O.W" />
        <TourBody>
          Your platform for career planning and professional growth. This short walkthrough
          covers your dashboard, how to generate your Career Report, and what each tab does.
        </TourBody>
        <div
          style={{
            padding: '12px 14px',
            borderRadius: 6,
            background: COLORS.bgAlt,
            border: `1px solid ${COLORS.border}`,
            fontSize: 13,
            color: COLORS.textMuted,
          }}
        >
          Click <strong style={{ color: COLORS.navy }}>Next</strong> to get started.
        </div>
      </div>
    ),
  },
  {
    target: '[data-tour="dashboard"]',
    placement: 'right',
    content: (
      <div style={{ width: '100%' }}>
        <TourHeader icon={LayoutDashboard} eyebrow="Overview" title="Your Dashboard" />
        <TourBody>This is your central hub for all career planning activity.</TourBody>
        <TourList
          items={[
            <><strong style={{ color: COLORS.navy }}>Career Map</strong> — explore career paths</>,
            <><strong style={{ color: COLORS.navy }}>Reports</strong> — generate your career report</>,
            <><strong style={{ color: COLORS.navy }}>Portfolio &amp; Skills</strong> — track your progress</>,
          ]}
        />
      </div>
    ),
  },
  {
    target: 'body',
    placement: 'center',
    content: (
      <div style={{ width: '100%' }}>
        <TourHeader icon={FileText} eyebrow="Step 1" title="Generate Your Career Report" />
        <TourBody>
          Before exploring other features, create your Career Journey Report. This unlocks the
          rest of the dashboard.
        </TourBody>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 8,
            padding: '14px 16px',
          }}
        >
          <StepBadge n={1} label='Open the "Career Journey Report" tab' />
          <StepBadge n={2} label="Select your profile type" />
          <div style={{ marginLeft: 32 }}>
            <TourList
              items={[
                'Student — currently studying (10th–12th, college)',
                'Fresher — recent graduate seeking a first job',
                'Professional — currently working',
              ]}
            />
          </div>
          <StepBadge n={3} label="Fill in the fields for your profile type" />
          <StepBadge n={4} label='Click "Generate Report"' />
        </div>
      </div>
    ),
  },
  {
    target: 'body',
    placement: 'center',
    content: (
      <div style={{ width: '100%' }}>
        <TourHeader icon={Map} eyebrow="Explore" title="Career Map" />
        <TourBody>Explore career paths and track your journey stage by stage.</TourBody>
        <TourList
          items={[
            'Select a career category — professional, creative arts, teaching, and more',
            'Choose a specific career — CA, doctor, engineer, content creator, and more',
            'View every stage required for your chosen career',
            'Mark stages complete as you progress',
            'Monitor your overall completion percentage',
          ]}
        />
        <TourNote>Example — select Professional → CA to see stages like 10+2 Commerce, CPT Exam, IPCC.</TourNote>
      </div>
    ),
  },
  {
    target: 'body',
    placement: 'center',
    content: (
      <div style={{ width: '100%' }}>
        <TourHeader icon={Route} eyebrow="Plan" title="Career Roadmap" />
        <TourBody>A visual, step-by-step view of your complete career journey.</TourBody>
        <TourList
          items={[
            'Choose your profile type — student, fresher, or professional',
            'Follow the step-by-step roadmap for your path',
            'See completed versus pending steps',
            'Track your overall completion percentage',
          ]}
        />
        <TourNote locked>Generate your Career Journey Report.</TourNote>
      </div>
    ),
  },
  {
    target: 'body',
    placement: 'center',
    content: (
      <div style={{ width: '100%' }}>
        <TourHeader icon={Target} eyebrow="Track" title="Skill Tracker" />
        <TourBody>Identify and track your skills, personality traits, and career interests.</TourBody>
        <TourList
          items={[
            'Personality traits — select up to 20 that describe you (e.g. creative, analytical, leadership)',
            'Career domains — choose your areas of interest (e.g. technology, healthcare, finance)',
            'Professional skills (for professionals) — soft skills and hard skills',
          ]}
        />
        <TourNote>Accurate traits help match you with more relevant opportunities.</TourNote>
      </div>
    ),
  },
  {
    target: '[data-tour="mentor"]',
    placement: 'right',
    content: (
      <div style={{ width: '100%' }}>
        <TourHeader icon={Users} eyebrow="Connect" title="Mentor Support" />
        <TourBody>Connect with industry experts and get personalized guidance for your career journey.</TourBody>
        <TourList
          items={[
            'Browse mentor profiles — view expertise, ratings, and specializations',
            'Ask questions — submit career-related queries directly to mentors',
            'Track your questions — monitor all questions in "My Questions"',
            'Get expert answers — receive detailed responses and guidance',
            'Provide feedback — rate and review mentor answers',
          ]}
        />
        <div style={{ marginTop: 12, padding: 12, background: COLORS.blue.bg, border: `1px solid ${COLORS.blue.border}`, borderRadius: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.blue.text, marginBottom: 6 }}>💡 How it works:</div>
          <ol style={{ fontSize: 13, color: COLORS.blue.text, marginLeft: 16, lineHeight: 1.5 }}>
            <li>Click on any mentor card to view their profile</li>
            <li>Click "Ask Question" to submit your query</li>
            <li>Mentor reviews and answers your question</li>
            <li>Get notified when you receive an answer</li>
            <li>Rate the answer and provide feedback</li>
          </ol>
        </div>
        <TourNote>Be specific with your questions to get the most helpful answers!</TourNote>
      </div>
    ),
  },
  {
    target: '[data-tour="notes"]',
    placement: 'right',
    content: (
      <div style={{ width: '100%' }}>
        <TourHeader icon={FileText} eyebrow="Organize" title="Personal Notes" />
        <TourBody>Create and organize notes for career planning, learning, and reminders.</TourBody>
        <TourList
          items={[
            'Create notes — write down ideas, reminders, and important information',
            'Color-coded — organize with 6 colors (yellow, pink, green, blue, purple, orange)',
            'Pin important notes — keep key notes at the top',
            'Edit anytime — update your notes whenever needed',
            'Auto-save — notes are saved automatically as you type',
          ]}
        />
        <div style={{ marginTop: 12, padding: 12, background: COLORS.purple.bg, border: `1px solid ${COLORS.purple.border}`, borderRadius: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.purple.text, marginBottom: 6 }}>📚 Great for:</div>
          <div style={{ fontSize: 13, color: COLORS.purple.text, lineHeight: 1.6 }}>
            Career goals • Interview prep • Learning resources • Meeting notes • Daily tasks • Skill development plans
          </div>
        </div>
        <TourNote>Use different colors to categorize notes by topic or priority!</TourNote>
      </div>
    ),
  },
  {
    target: '[data-tour="profile"]',
    placement: 'right',
    content: (
      <div style={{ width: '100%' }}>
        <TourHeader icon={UserCircle2} eyebrow="Account" title="Your Profile" />
        <TourBody>Manage your personal information and account settings.</TourBody>
        <TourList
          items={[
            'Edit personal details and contact information',
            'Change your password',
            'Upload a profile picture',
            'View your achievements',
          ]}
        />
      </div>
    ),
  },
  {
    target: 'body',
    placement: 'center',
    content: (
      <div style={{ width: '100%' }}>
        <TourHeader icon={Rocket} eyebrow="Ready" title="You're All Set" />
        <TourBody>Here's the recommended order to get the most out of F.O.W:</TourBody>
        <div
          style={{
            border: `1px solid ${COLORS.border}`,
            borderRadius: 8,
            padding: '14px 16px',
            marginBottom: 14,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <StepBadge n={1} label="Generate your Career Journey Report" />
            <StepBadge n={2} label="Explore the Career Map" />
            <StepBadge n={3} label="Complete the Career Roadmap" />
            <StepBadge n={4} label="Build your Education Portfolio" />
            <StepBadge n={5} label="Track your skills" />
            <StepBadge n={6} label="Explore opportunities" />
          </div>
        </div>
        <TourNote>Complete every field for the most accurate recommendations, and check the Opportunities tab regularly for new listings.</TourNote>
        <div style={{ marginTop: 14, textAlign: 'center', fontSize: 12.5, color: COLORS.textMuted }}>
          You can restart this tour anytime from the help icon.
        </div>
      </div>
    ),
  },
];

// ---------------------------------------------------------------------------
// Responsive helpers
// ---------------------------------------------------------------------------
// Joyride's `styles` prop only accepts static inline-style objects, so we
// can't write real CSS media queries there. Instead we inject a small
// stylesheet once (scoped to Joyride's own classnames) that handles the
// breakpoints, and keep the inline `styles` prop as sane, already-responsive
// fallbacks (using vw/min()/clamp()) for browsers/SSR before the stylesheet
// mounts.
const injectResponsiveTourStyles = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById('tour-responsive-styles')) return;

  const style = document.createElement('style');
  style.id = 'tour-responsive-styles';
  style.innerHTML = `
    .react-joyride__tooltip {
      box-sizing: border-box !important;
      max-height: 85vh !important;
      overflow-y: auto !important;
      -webkit-overflow-scrolling: touch;
    }
    .react-joyride__tooltip::-webkit-scrollbar {
      width: 6px;
    }
    .react-joyride__tooltip::-webkit-scrollbar-thumb {
      background: ${COLORS.border};
      border-radius: 3px;
    }
    @media (max-width: 640px) {
      .react-joyride__tooltip {
        width: 92vw !important;
        max-width: 92vw !important;
        padding: 16px !important;
        font-size: 13px !important;
      }
      .react-joyride__tooltip h3 {
        font-size: 16px !important;
      }
    }
    @media (max-width: 400px) {
      .react-joyride__tooltip {
        padding: 12px !important;
      }
    }
  `;
  document.head.appendChild(style);
};

export const TourProvider = ({ children }) => {
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [tourCompleted, setTourCompleted] = useState(() => {
    return localStorage.getItem('tourCompleted') === 'true';
  });

  // Inject the responsive stylesheet once on mount.
  useState(() => {
    injectResponsiveTourStyles();
    return null;
  });

  const handleJoyrideEvent = useCallback((data) => {
    const { action, index, status, type } = data;

    if (type === EVENTS.TARGET_NOT_FOUND) {
      const nextIndex = action === ACTIONS.PREV ? index - 1 : index + 1;
      setStepIndex(nextIndex);
      return;
    }

    if (type === EVENTS.STEP_AFTER) {
      const nextIndex = action === ACTIONS.PREV ? index - 1 : index + 1;
      setStepIndex(nextIndex);
    } else if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setRun(false);
      setStepIndex(0);
      setTourCompleted(true);
      localStorage.setItem('tourCompleted', 'true');
    }
  }, []);

  const startTour = useCallback(() => {
    setRun(true);
    setStepIndex(0);
  }, []);

  const resetTour = useCallback(() => {
    setTourCompleted(false);
    localStorage.removeItem('tourCompleted');
    setRun(true);
    setStepIndex(0);
  }, []);

  const value = {
    startTour,
    resetTour,
    tourCompleted,
  };

  return (
    <TourContext.Provider value={value}>
      {children}
      <Joyride
        steps={steps}
        run={run}
        stepIndex={stepIndex}
        continuous
        scrollToFirstStep
        disableScrolling={false}
        onEvent={handleJoyrideEvent}
        floaterProps={{
          disableFlip: false,
          options: {
            // Popper.js v2 modifier shape (react-floater passes these straight
            // through). This is what actually keeps the tooltip box inside
            // the browser viewport — without it, Popper only cares about
            // anchoring next to the target and ignores our maxHeight, so the
            // card can still render past the bottom of the screen.
            preventOverflow: {
              rootBoundary: 'viewport',
              altBoundary: true,
              padding: 12,
              tether: false,
            },
            flip: {
              rootBoundary: 'viewport',
              altBoundary: true,
              padding: 12,
              fallbackPlacements: ['top', 'bottom', 'left', 'right', 'auto'],
            },
          },
        }}
        options={{
          primaryColor: COLORS.accent,
          zIndex: 10000,
          showProgress: true,
          showSkipButton: true,
          skipBeacon: true,
          disableOverlayClose: true,
          spotlightClicks: false,
        }}
        styles={{
          overlay: {
            backgroundColor: 'rgba(15, 24, 48, 0.55)',
          },
          tooltip: {
            borderRadius: 10,
            fontSize: 15,
            // Responsive fallback: never wider than the viewport, caps at 560px.
            width: 'min(560px, 92vw)',
            maxWidth: '92vw',
            // Never taller than the viewport — scrolls internally instead of
            // running off the bottom of the screen. Kept a little under 85vh
            // so there's always breathing room even after Popper repositions
            // the card (see floaterProps below).
            maxHeight: '80vh',
            overflowY: 'auto',
            boxSizing: 'border-box',
            // Padding scales down smoothly on narrower screens instead of
            // staying a fixed 26px.
            padding: 'clamp(14px, 4vw, 26px)',
            boxShadow: '0 12px 32px rgba(15, 24, 48, 0.18)',
          },
          tooltipContainer: {
            textAlign: 'left',
          },
          tooltipContent: {
            padding: '0',
          },
          tooltipFooter: {
            marginTop: 18,
            paddingTop: 16,
            borderTop: `1px solid ${COLORS.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 8,
          },
          tooltipFooterSpacer: {
            flex: 1,
          },
          buttonNext: {
            backgroundColor: COLORS.navyLight,
            borderRadius: 6,
            padding: '9px 18px',
            fontSize: '13.5px',
            fontWeight: '600',
            boxShadow: 'none',
            outline: 'none',
          },
          buttonBack: {
            color: COLORS.navy,
            marginRight: 10,
            padding: '9px 14px',
            fontSize: '13.5px',
            fontWeight: '600',
            outline: 'none',
          },
          buttonSkip: {
            color: COLORS.textMuted,
            fontSize: '13px',
            padding: '9px 4px',
            outline: 'none',
          },
          buttonClose: {
            display: 'none',
          },
          options: {
            arrowColor: '#fff',
          },
        }}
        locale={{
          back: 'Previous',
          close: 'Close',
          last: 'Finish',
          next: 'Next',
          skip: 'Skip Tour',
        }}
      />
    </TourContext.Provider>
  );
};